import { useMemo } from "react";
import styled from "styled-components";

import { hexToRgba } from "utils/color";
import { RealtimeChartIcon } from "assets/icons/businessDashboard";
import type { ChangeRate } from "types/BusinessDashboard";

import { ChangeIndicator } from "./change-indicator";

interface BreakdownItem {
  name: string;
  amount: string;
  changeRate: ChangeRate;
  color: string;
}

interface RevenueCardData {
  title: string;
  amount: string;
  changeRate: ChangeRate;
  category: string;
  breakdown: BreakdownItem[];
}

type RevenueCardProps = {
  type: string;
  data: RevenueCardData;
  bg: string;
  bgGradient: string;
  categoryColor: string;
  period: string;
};

const Article = styled.article`
  overflow: hidden;
  padding-top: 0;
  padding-bottom: 0;
  gap: 0;
  border: none;
  box-shadow: none;
  border-top-left-radius: 28px;
  border-top-right-radius: 28px;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
  background: none;
`;

const CardBg = styled.div<{ bg: string }>`
  ${({ bg }) => bg && `background: ${bg};`}
  color: #fff;
  position: relative;
`;

const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  padding: 60px 16px 0 16px;
`;

const CardHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const CardPeriod = styled.div`
  color: #ffffff99;
  font-size: 13.95px;
  font-weight: 500;
  line-height: 20.93px;
  letter-spacing: -0.17px;
`;

const CardAmount = styled.h3`
  font-size: 27.9px;
  font-weight: bold;
  line-height: 34.88px;
  letter-spacing: -0.35px;
  margin: 0;
`;

const ChartIconRow = styled.div`
  height: 30px;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  margin-bottom: 12px;
  padding-right: 28px;
`;

const CardFooter = styled.div<{ bgGradient: string }>`
  ${({ bgGradient }) => bgGradient && `background: ${bgGradient};`}
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  height: 38px;
  font-size: 12px;
  font-weight: 700;
  line-height: 14px;
  letter-spacing: -0.2px;
`;

const CardFooterSection = styled.div`
  display: flex;
  align-items: center;
`;

const CardFooterLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  line-height: 14px;
  letter-spacing: -0.2px;
`;

const CardFooterCategory = styled.span<{ color: string }>`
  margin-left: 0.25rem;
  font-size: 12px;
  font-weight: 700;
  line-height: 14px;
  letter-spacing: -0.2px;
  color: ${({ color }) => color};
`;

const CardBody = styled.div`
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
  width: 100%;
  padding: 20px 12px;
  background-color: white;
`;

const CardBodyTitle = styled.p`
  font-size: 12px;
  font-weight: 700;
  color: #6c727f;
  line-height: 14px;
  letter-spacing: -0.2px;
  padding-left: 1rem;
  margin-bottom: 12px;
`;

const BreakdownList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BreakdownItemRow = styled.div<{
  highlight?: boolean;
  highlightColor?: string;
}>`
  display: flex;
  align-items: flex-start;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  justify-content: space-between;
  transition: all 0.2s;
  border-radius: 8px;
  padding-left: 13.95px;
  padding-right: 13.95px;
  background: ${({ highlight, highlightColor }) =>
    highlight ? highlightColor : "transparent"};
`;

const BreakdownItemInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0 6px;
`;

const BreakdownItemDot = styled.div<{ color: string }>`
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  background: ${({ color }) => color};
`;

const BreakdownItemName = styled.span<{ color: string }>`
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: -0.2px;
  color: ${({ color }) => color};
`;

const BreakdownItemRight = styled.div`
  text-align: right;
`;

const BreakdownItemAmount = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: -0.2px;
  color: #26282b;
`;

export const RevenueCard = ({
  type,
  data,
  bg,
  bgGradient,
  categoryColor,
  period,
}: RevenueCardProps) => {
  const biggestItem = useMemo(() => {
    if (!data?.breakdown || data.breakdown.length === 0) {
      return null;
    }
    const parseAmount = (amountStr: string) =>
      parseInt(amountStr.replace(/[^0-9]/g, ""), 10);
    return data.breakdown.reduce((maxItem, currentItem) => {
      return parseAmount(currentItem.amount) > parseAmount(maxItem.amount)
        ? currentItem
        : maxItem;
    });
  }, [data?.breakdown]);

  const highlightBgColor = hexToRgba(categoryColor, 0.08);

  return (
    <Article>
      <CardBg bg={bg}>
        <CardHeader>
          <CardHeaderRow>
            <CardPeriod>{period}</CardPeriod>
          </CardHeaderRow>
          <CardAmount>{data.amount}</CardAmount>
        </CardHeader>
        <ChartIconRow>
          <RealtimeChartIcon />
        </ChartIconRow>
        <CardFooter bgGradient={bgGradient}>
          <CardFooterSection>
            <CardFooterLabel>
              {type === "day"
                ? "전일대비"
                : type === "week"
                  ? "전주대비"
                  : "전월 동기간 대비"}
            </CardFooterLabel>
            <div style={{ marginLeft: 4 }}>
              <ChangeIndicator
                green
                showKrUnit
                showIcon={false}
                changeRate={data.changeRate}
              />
            </div>
          </CardFooterSection>
          <span>
            <CardFooterLabel>주력 매출</CardFooterLabel>
            <CardFooterCategory color={categoryColor}>
              {data.category}
            </CardFooterCategory>
          </span>
        </CardFooter>
      </CardBg>
      <CardBody>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <CardBodyTitle>이용권 종류별</CardBodyTitle>
          <BreakdownList>
            {data.breakdown.map((item: BreakdownItem, index: number) => {
              const isBiggest = item.name === biggestItem?.name;
              const dotColor =
                item.name === "개인레슨"
                  ? "#4E23A8"
                  : item.name === "회원권"
                    ? "#003AAC"
                    : item.color;
              return (
                <BreakdownItemRow
                  key={index}
                  highlight={isBiggest}
                  highlightColor={highlightBgColor}
                >
                  <BreakdownItemInfo>
                    <BreakdownItemDot color={dotColor} />
                    <BreakdownItemName color={dotColor}>
                      {item.name}
                    </BreakdownItemName>
                  </BreakdownItemInfo>
                  <BreakdownItemRight>
                    <BreakdownItemAmount>{item.amount}</BreakdownItemAmount>
                    <ChangeIndicator
                      showIcon={false}
                      changeRate={item.changeRate}
                    />
                  </BreakdownItemRight>
                </BreakdownItemRow>
              );
            })}
          </BreakdownList>
        </div>
      </CardBody>
    </Article>
  );
};
